import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { Twitter, Linkedin, Instagram, Mail, Phone, MapPin, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_dfd40c82-155c-446f-b6a2-3614c96b8638/artifacts/l43p30e7_Frame%20%283%29%20%282%29.png";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, source: "footer" });
      setDone(true);
      setEmail("");
      toast.success("Subscribed! We'll keep you posted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to subscribe");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="footer-newsletter" className="space-y-3">
      <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-full pl-4 pr-1 py-1 focus-within:border-orange-500 transition">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          data-testid="newsletter-email"
          className="flex-1 bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none py-2"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="newsletter-submit"
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full font-semibold inline-flex items-center gap-1 text-sm disabled:opacity-50 transition"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {busy ? "" : "Join"}
        </button>
      </div>
      {done && <div className="text-xs text-green-400">You're in. Welcome aboard.</div>}
    </form>
  );
};

const Footer = () => {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);

  React.useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  return (
    <footer data-testid="site-footer" className="bg-zinc-950 text-zinc-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO} alt="Reptile Global" className="h-10 w-auto object-contain bg-white rounded p-1" />
            <span className="font-display text-2xl font-black text-white">
              Reptile<span className="text-orange-500">Global</span>
            </span>
          </div>
          <p className="text-sm leading-relaxed max-w-sm">
            {settings?.description || "Software that scales businesses across the globe."}
          </p>
          <div className="mt-6 space-y-2 text-sm">
            {settings?.contact_email && (
              <div className="flex items-center gap-2"><Mail size={14} className="text-orange-400" /> {settings.contact_email}</div>
            )}
            {settings?.contact_phone && (
              <div className="flex items-center gap-2"><Phone size={14} className="text-orange-400" /> {settings.contact_phone}</div>
            )}
            {settings?.address && (
              <div className="flex items-center gap-2"><MapPin size={14} className="text-orange-400" /> {settings.address}</div>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-white font-display font-bold mb-4 text-sm uppercase tracking-widest">Products</h4>
          <ul className="space-y-2 text-sm">
            {products.slice(0, 7).map((p) => (
              <li key={p.id}><Link to={`/products/${p.slug}`} className="hover:text-orange-400">{p.name}</Link></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-white font-display font-bold mb-4 text-sm uppercase tracking-widest">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-orange-400">About</Link></li>
            <li><Link to="/services" className="hover:text-orange-400">Services</Link></li>
            <li><Link to="/blog" className="hover:text-orange-400">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-orange-400">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-white font-display font-bold mb-4 text-sm uppercase tracking-widest">Stay in the loop</h4>
          <p className="text-sm mb-4">Product updates, engineering notes, occasional offers. No spam.</p>
          <Newsletter />
          <div className="flex items-center gap-3 mt-6">
            {settings?.social_links?.twitter && (
              <a href={settings.social_links.twitter} target="_blank" rel="noreferrer" className="p-2 rounded-full border border-zinc-800 hover:border-orange-500 hover:text-orange-400 transition"><Twitter size={16} /></a>
            )}
            {settings?.social_links?.linkedin && (
              <a href={settings.social_links.linkedin} target="_blank" rel="noreferrer" className="p-2 rounded-full border border-zinc-800 hover:border-orange-500 hover:text-orange-400 transition"><Linkedin size={16} /></a>
            )}
            {settings?.social_links?.instagram && (
              <a href={settings.social_links.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-full border border-zinc-800 hover:border-orange-500 hover:text-orange-400 transition"><Instagram size={16} /></a>
            )}
          </div>
          <div className="mt-6 text-xs">
            <Link to="/admin/login" className="hover:text-orange-400" data-testid="footer-admin-link">Admin Portal →</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} Reptile Global. All rights reserved.</div>
          <div>Crafted with care, shipped with speed.</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
